import React, { createContext, useEffect, useState, useCallback } from "react";
import client from "../api/client";

export const LocationContext = createContext({
  coords: null,
  setCoords: () => {},
  permission: "unknown",
  locationLabel: null,
  searchPlaces: async (q) => [],
  setLocationFromPlace: async (place) => {},
  getRecentLocations: () => [],
  getPopularLocations: () => [],
});

const RECENT_KEY = "recent_locations_v1";
const MAX_RECENT = 6;

export const LocationProvider = ({ children }) => {
  const [coords, setCoordsState] = useState(null);
  const [permission, setPermission] = useState("unknown");
  const [locationLabel, setLocationLabel] = useState(null);

  // set coords and optionally reverse lookup
  const setCoords = useCallback(async (c, skipReverse = false) => {
    setCoordsState(c);
    if (!skipReverse && c) {
      try {
        const res = await client.get(`/api/geo/reverse?lat=${c.lat}&lng=${c.lng}`);
        if (res?.data?.label) setLocationLabel(res.data.label);
      } catch (err) {
        // ignore
      }
    }
  }, []);

  // search via backend
  const searchPlaces = useCallback(async (q, limit = 6) => {
    if (!q) return [];
    try {
      const res = await client.get(`/api/geo/search?q=${encodeURIComponent(q)}&limit=${limit}`);
      return res.data.results || [];
    } catch (err) {
      console.warn("searchPlaces error", err);
      return [];
    }
  }, []);

  const addRecentLocation = (loc) => {
    if (!loc) return;
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      // dedupe by display_name
      const filtered = raw.filter((r) => r.display_name !== loc.display_name);
      filtered.unshift(loc);
      const sliced = filtered.slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(sliced));
    } catch (err) {
      console.warn("recent loc store err", err);
    }
  };

  const getRecentLocations = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const getPopularLocations = () => {
    // static list - you can move to backend later
    return [
      { display_name: "Chennai, Tamil Nadu, India", lat: 13.0827, lon: 80.2707 },
      { display_name: "Mumbai, Maharashtra, India", lat: 19.0760, lon: 72.8777 },
      { display_name: "Bengaluru, Karnataka, India", lat: 12.9716, lon: 77.5946 },
      // ... add more
    ];
  };

  const setLocationFromPlace = async (place) => {
    if (!place) return;
    const { lat, lon, display_name } = place;
    await setCoords({ lat, lng: lon }, true); // skip reverse
    setLocationLabel(display_name || place.display_name);
    addRecentLocation(place);
  };

  // initial geolocation request (same as earlier)
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setPermission("denied");
      return;
    }
    const requestLocation = () => {
      setPermission("prompt");
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          await setCoords(c, false);
          setPermission("granted");
        },
        () => {
          setPermission("denied");
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    };
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((perm) => {
        setPermission(perm.state);
        if (perm.state === "granted" || perm.state === "prompt") requestLocation();
        perm.onchange = () => {
          setPermission(perm.state);
          if (perm.state === "granted") requestLocation();
        };
      }).catch(requestLocation);
    } else {
      requestLocation();
    }
  }, [setCoords]);

  return (
    <LocationContext.Provider
      value={{
        coords,
        setCoords,
        permission,
        locationLabel,
        searchPlaces,
        setLocationFromPlace,
        getRecentLocations,
        getPopularLocations,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
